import { Table, PageHeader } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ExportExcel from "Components/export-excel";
import { createComponentStore, useStoreState } from "easy-peasy";
import { PARENT_JOB_TYPE, CURRENCY, GENDERS } from "Constants";

import {
  KinderGartenReportStoreModel,
  ACTION_TYPES,
} from "store/models/reports/kinder-garten";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import FourOThree from "Pages/private/403";
import { sort } from "Utils";

const useKinderGartenReportsStore = createComponentStore(
  KinderGartenReportStoreModel
);

export default function KinderGartenReportPage() {
  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(
    abilities,
    AUTH_ACTIONS.READ,
    "Kreş Raporları"
  );

  const [store, actions] = useKinderGartenReportsStore();
  const { fetch } = actions;
  const { process, data } = store;
  const [filteredValue, setFilteredValue] = useState({
    pageEnabled: true,
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    getExcelColumns();
  }, [data]);

  const columns = [
    {
      title: "Öğrenci",
      dataIndex: "name",
      key: "name",
      width: 140,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          (a.name || "") + " " + (a.surname || ""),
          (b.name || "") + " " + (b.surname || "")
        ),
      render: (text, record) => {
        return record.name + " " + record.surname;
      },
    },

    {
      title: "Cinsiyet",
      dataIndex: "gender",
      key: "gender",
      width: 80,
      ellipsis: true,
      render: (text, record) => GENDERS[record.gender] || "",
      sorter: (a, b) => sort(GENDERS[a.gender], GENDERS[b.gender]),
    },

    {
      title: "Doğum Yeri",
      dataIndex: "birthPlace",
      key: "birthPlace",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => sort(a.birthPlace, b.birthPlace),
    },

    {
      title: "Velisi",
      dataIndex: "saintName",
      key: "saintName",
      width: 170,
      ellipsis: true,
      sorter: (a, b) =>
        sort(
          a.saintName + " " + a.saintSurname,
          b.saintName + " " + b.saintSurname
        ),
      render: (text, record) =>
        record.saintName +
        " " +
        record.saintSurname +
        " (" +
        record.intimacy +
        ")",
    },

    {
      title: "Veli Personel Türü",
      dataIndex: "civilServantOther",
      key: "civilServantOther",
      width: 130,
      ellipsis: true,
      render: (text, record) => PARENT_JOB_TYPE[record.civilServantOther] || "",
      sorter: (a, b) =>
        sort(
          PARENT_JOB_TYPE[a.civilServantOther] || "",
          PARENT_JOB_TYPE[b.civilServantOther] || ""
        ),
    },

    {
      title: "Veli Telefon No",
      dataIndex: "saintPhone",
      key: "saintPhone",
      width: 130,
      ellipsis: true,
    },

    {
      title: "Sözleşme Açıklaması",
      dataIndex: "contractDescription",
      key: "contractDescription",
      width: 160,
      ellipsis: true,
      sorter: (a, b) => sort(a.contractDescription, b.contractDescription),
    },

    {
      title: "Koşul Tipi Açıklaması",
      dataIndex: "conditionTypeDescription",
      key: "conditionTypeDescription",
      width: 150,
      ellipsis: true,
      sorter: (a, b) =>
        sort(a.conditionTypeDescription, b.conditionTypeDescription),
    },

    {
      title: "Sözleşme Miktarı",
      dataIndex: "documentAmount",
      key: "documentAmount",
      width: 150,
      ellipsis: true,
      render: (text, record) =>
        (record.documentAmount || "") +
        (CURRENCY[record.documentCurrency] || ""),
      sorter: (a, b) => sort(a.documentAmount, b.documentAmount),
    },

    {
      title: "Kreşe Kabul Tarihi",
      dataIndex: "dateOfApprovalKinderGarten",
      key: "dateOfApprovalKinderGarten",
      width: 160,
      ellipsis: true,
      sorter: (a, b) =>
        sort(a.dateOfApprovalKinderGarten, b.dateOfApprovalKinderGarten),
      render: (text, record) => {
        return moment(record.dateOfApprovalKinderGarten).format("DD.MM.YYYY");
      },
    },
  ];

  const getExcelColumns = () => {
    return columns.map((d) => d.title);
  };

  const getExcelRows = () => {
    let rows = [];
    store.data.forEach((d) => {
      let i = [];
      i.push((d.name || "") + " " + (d.name || ""));
      i.push(GENDERS[d.gender] || "");
      i.push(d.birthPlace || "");
      i.push(
        (d.saintName || "") +
          " " +
          (d.saintSurname || "") +
          " (" +
          (d.intimacy || "") +
          ")"
      );
      i.push(PARENT_JOB_TYPE[d.civilServantOther] || "");
      i.push(d.saintPhone || "");
      i.push(d.contractDescription || "");
      i.push(d.conditionTypeDescription || "");
      i.push((d.documentAmount || "") + (CURRENCY[d.documentCurrency] || ""));
      i.push(moment(d.dateOfApprovalKinderGarten).format("DD.MM.YYYY") || "");
      rows.push(i);
    });

    return rows;
  };

  return IsAuthorized ? (
    <>
      <PageHeader
        title="Kreş Raporları"
        extra={[
          <ExportExcel
            columnsData={getExcelColumns()}
            key={data}
            disabled={data.length < 1}
            rowsData={getExcelRows()}
            loading={store.process === ACTION_TYPES.FETCHING_DATA}
            filename={
              "Kreş Raporları_" + moment(new Date()).format("DD-MM-YYYY")
            }
          />,
        ]}
      />
      <Table
        style={{ margin: "0 24px" }}
        rowKey={(row) => row.id}
        loading={process === ACTION_TYPES.FETCHING_DATA}
        dataSource={data}
        columns={columns}
        size="small"
        bordered
        showSorterTooltip={false}
        scroll={{ x: 1000, scrollToFirstRowOnChange: true }}
        pagination={{
          pageSize: 20,
          total: data.length,
          showSizeChanger: false,
          showTotal: (total, range) => {
            return `Toplam ${total} sonuçtan ${range.join(
              "-"
            )} arası gösteriliyor.`;
          },
        }}
      />
    </>
  ) : (
    <FourOThree />
  );
}
